import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import Swal from "sweetalert2";
import moment from "moment";

//import components
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer2";
import CloseClassModal from "../../components/Common/CloseClassModal";
import AddStudentModel from "../../components/Common/AddStudentModel";
import EmergencyStudentModel from "../../components/Common/EmergencyStudentModel";
import { Nav, NavItem, NavLink } from "reactstrap";

const DatatableTables = () => {
  const [tableData, setTableData] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [compStudents, setCompStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const getFormattedDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const todayFormatted = getFormattedDate(today);
  const tomorrowFormatted = getFormattedDate(tomorrow);
  const formattedToday = today.toISOString().split('T')[0];
  const [classDate, setClassDate] = useState(formattedToday);
  const [selectedDayTab, setSelectedDayTab] = useState("final-list");
  const [page, setPage] = useState(1);
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState("");
  const [countPerPage, setCountPerPage] = useState(7);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Asc");
  const [showModal, setShowModal] = useState(false);
  const [formData1, setFormData1] = useState({});
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [formData2, setFormData2] = useState({});
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [formData3, setFormData3] = useState({});
  const [modalTimeSlots, setModalTimeSlots] = useState([]);
  const [modalBatchStudentCount, setModalBatchStudentCount] = useState({});
  const [attendanceTakenSlots, setAttendanceTakenSlots] = useState([]);
  const [isClassCancelled, setIsClassCancelled] = useState(false);
  const [isClassHoliday, setIsClassHoliday] = useState(false);
  const token = localStorage.getItem('authToken');

  const fetchData = useCallback(async () => {
    try {
      if (!classDate || !timeSlot) return;

      if (!canFetchData()) {
        setTableData([]);
        return;
      }

      setLoading(true);

      const query = new URLSearchParams({
        classDate,
        timeSlot,
        page,
        countPerPage,
        sortingBy,
        direction,
      }).toString();

      const apiEndpoint =
        selectedDayTab === "temp-list"
          ? "GetStudentsTempListByDate"
          : "GetStudentsFinalListByDate";

      const res = await fetch(`https://api.kunalsdrumclass.com/api/${apiEndpoint}?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);

      const cancelled = result?.recordsets?.[2]?.[0]?.IsClassCancelled === 1;
      setIsClassCancelled(cancelled);

      const holiday = result?.recordsets?.[3]?.[0]?.IsClassHoliday === 1;
      setIsClassHoliday(holiday);

    } catch (error) {
      console.error("Error loading final list:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDayTab, classDate, page, timeSlot, countPerPage, sortingBy, direction, token]);


  const holidayMessage = useMemo(() => {
    if (!isClassHoliday) return "";

    return selectedDayTab === "final-list"
      ? "Today is a Holiday. All classes are closed."
      : "Tomorrow is a Holiday. All classes will remain closed.";
  }, [isClassHoliday, selectedDayTab]);

  const handleDayTabChange = (tab) => {
    setTableData([]);
    setTotalRecords(0);
    setSelectedDayTab(tab);

    const selectedDate =
      tab === "final-list"
        ? today
        : tomorrow;

    setIsClassHoliday(false);
    setClassDate(getFormattedDate(selectedDate));
    setPage(1);
  };

  const canFetchData = () => {
    if (selectedDayTab === "final-list") {
      const now = moment();
      const currentMinutes = now.hours() * 60 + now.minutes();
      const todayDay = now.day();

      if (todayDay >= 1 && todayDay <= 5) {
        return currentMinutes >= 12 * 60;
      }

      return currentMinutes >= 8 * 60;
    }

    if (selectedDayTab === "temp-list") {
      const now = moment();
      const tomorrowDay = now.clone().add(1, "day").day();
      const currentMinutes = now.hours() * 60 + now.minutes();

      const startTime = 0;
      const endTime = 24 * 60;

      return (
        currentMinutes >= startTime &&
        currentMinutes < endTime
      );
    }

    return false;
  };

  const fetchBatchSlots = useCallback(async (selectedDate) => {
    try {
      const dayName = moment(selectedDate).format("dddd");
      const res = await fetch(
        `https://api.kunalsdrumclass.com/api/GetBatchSlotsByDay?day=${dayName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();
      const slots = json?.recordsets?.[0] || [];
      setTimeSlots(slots);

      if (slots.length > 0) {
        setTimeSlot(slots[0].Batch_Time);
      } else {
        setTimeSlot("");
      }

    } catch (error) {
      console.error("Batch slot API error:", error);
    }
  }, [token]);

  const fetchAttendanceTakenSlots = async () => {
    try {
      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAttendanceTakenBatchSlotsByDate?classDate=${moment().format("YYYY-MM-DD")}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();
      const slots = json?.recordsets?.[0] || [];
      setAttendanceTakenSlots(
        slots.map(item => item.Batch_Time)
      );

    } catch (err) {
      console.error(err);
    }
  };

  const fetchModalBatchData = useCallback(async (selectedDate) => {
    try {
      const dayName = moment(selectedDate).format("dddd");
      const slotRes = await fetch(`https://api.kunalsdrumclass.com/api/GetBatchSlotsByDay?day=${dayName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const slotJson = await slotRes.json();
      const slots = slotJson?.recordsets?.[0] || [];

      setModalTimeSlots(slots);

      const query = new URLSearchParams({
        classDate: selectedDate,
        page: 1,
        countPerPage: 1000,
      }).toString();

      const countRes = await fetch(`https://api.kunalsdrumclass.com/api/GetStudentsFinalListByDate?${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const countJson = await countRes.json();
      const list = countJson?.recordsets?.[0] || [];
      const counts = {};

      list.forEach((item) => {
        counts[item.Time_Slot] =
          (counts[item.Time_Slot] || 0) + 1;
      });

      setModalBatchStudentCount(counts);

    } catch (error) {
      console.error("Modal batch fetch error:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchBatchSlots(classDate);
  }, [classDate, fetchBatchSlots]);

  useEffect(() => {
    if (showModal && formData1.classDate) {
      fetchModalBatchData(formData1.classDate);
    }
  }, [showModal, formData1.classDate, fetchModalBatchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res1 = await fetch("https://api.kunalsdrumclass.com/api/GetActiveStudents", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data1 = await res1.json();
        setAllStudents(data1?.recordsets?.[0] || []);

        const res2 = await fetch("https://api.kunalsdrumclass.com/api/GetCompClassQueueStudents", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data2 = await res2.json();
        setCompStudents(data2?.recordsets?.[0] || []);
      } catch (err) {
        console.error("Student list error", err);
      }
    };

    fetchStudents();
  }, []);

  const handlePageChange = (newPage) => setPage(newPage);

  const updateStudentAssignment = useCallback(async (rowData, studentId) => {
    try {
      if (rowData.Booking_Type === "Fixed") {
        const payload1 = {
          fixedStudentId: rowData.Student_Id,
          newCompStudentId: studentId,
          classDate: rowData.Class_Date,
          dayOfWeek: rowData.Day_Of_Week,
          timeSlot: rowData.Time_Slot,
        };
        await fetch("https://api.kunalsdrumclass.com/api/UpdateFixedWithCompStudent", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload1),
        });

        await Swal.fire({
          title: "Success!",
          text: "student updated successfully!",
          icon: "success",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-primary",
          },
        });

        fetchData();
      } else {
        const payload1 = {
          oldCompStudentId: rowData.Student_Id,
          newCompStudentId: studentId,
          classDate: rowData.Class_Date,
        };
        await fetch("https://api.kunalsdrumclass.com/api/UpdateCompStudent", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload1),
        });

        await Swal.fire({
          title: "Success!",
          text: "student updated successfully!",
          icon: "success",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-primary",
          },
        });

        fetchData();
      }

    } catch (error) {
      console.error("Update failed:", error);
    }
  }, [token]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const canEdit = useMemo(
    () => hasPermission(permissionMap, MODULES.FINALLIST, PERMISSIONS.EDIT),
    [permissionMap]
  );

  const handleStudentChange = async (rowIndex, studentId) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to updated this student?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    const rowData = tableData[rowIndex];
    await updateStudentAssignment(rowData, studentId);
  };

  const handleTimeSlotChange = (time) => {
    setTimeSlot(time);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const isEditableTime = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentMinutes = hours * 60 + minutes;

    if (day === 0 || day === 6) {
      const limit = 10 * 60;
      return currentMinutes < limit;
    }

    const limit = 16 * 60;
    return currentMinutes < limit;
  }, []);

  const openAddModal = () => {
    const defaultDate = moment().format("YYYY-MM-DD");
    setFormData1({
      bookingType: "",
      classDate: defaultDate,
      studentId: "",
      batchTime: "",
    });

    setShowModal(true);
  };

  const closeAddModal = () => {
    setShowModal(false);
  };

  const openCloseModal = async () => {
    await fetchAttendanceTakenSlots();
    setFormData3({
      batchTime: "",
      reason: "",
    });

    setShowCloseModal(true);
  };

  const closeCloseModal = () => {
    setShowCloseModal(false);
  };

  const openEmergencyModal = () => {
    setFormData2({
      bookingType: "",
      studentId: "",
      batchTime: "",
    });

    setShowEmergencyModal(true);
  };

  const closeEmergencyModal = () => {
    setShowEmergencyModal(false);
  };

  const batchStudentCount = useMemo(() => {
    const counts = {};

    tableData.forEach((item) => {
      const time = item.Time_Slot;

      counts[time] = (counts[time] || 0) + 1;
    });

    return counts;
  }, [tableData]);

  const getBookingClassDate = (batchTime) => {
    const now = moment();
    const startTime = batchTime.split("-")[0].trim();
    const todayBatch = moment(
      `${moment().format("YYYY-MM-DD")} ${startTime}`,
      ["YYYY-MM-DD h A", "YYYY-MM-DD h:mm A"]
    );

    const previousDayOpen = todayBatch
      .clone()
      .subtract(1, "day")
      .set({
        hour: 19,
        minute: 0,
        second: 0,
      });

    const closeTime = todayBatch
      .clone()
      .subtract(30, "minutes");

    if (now.isBetween(previousDayOpen, closeTime)) {
      return {
        classDate: todayBatch.format("YYYY-MM-DD"),
        day: todayBatch.format("dddd"),
      };
    }

    const nextBatch = todayBatch.clone().add(1, "day");

    return {
      classDate: nextBatch.format("YYYY-MM-DD"),
      day: nextBatch.format("dddd"),
    };
  };

  const handleCloseClass = async () => {
    try {
      setLoading(true);
      const payload = {
        classDate: moment().format("YYYY-MM-DD"),
        day: moment().format("dddd"),
        timeSlot: formData3.batchTime,
        reason: formData3.reason,
        isCompGiven: formData3.isCompGiven,
      };

      const payloads = {
        title: "Class Cancellation",
        body: payload.reason,
        date: payload.classDate,
        day: payload.day,
        timeSlot: payload.timeSlot,
      };

      await fetch(`https://api.kunalsdrumclass.com/api/SendCancelNotification`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payloads),
        }
      );

      await fetch(`https://api.kunalsdrumclass.com/api/CancelStudentFromFinalList`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      await Swal.fire({
        title: "Success!",
        text: "Class closed successfully!",
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      setLoading(false);
      closeCloseModal();
      fetchData();

    } catch (error) {
      console.error(error);
    }
  };

  const handleAddStudent = async () => {
    try {
      setLoading(true);
      if (formData1.bookingType === "Fixed") {
        const bookingData = getBookingClassDate(formData1.batchTime);
        const payload = {
          studentId: formData1.studentId,
          classDate: bookingData.classDate,
          dayOfWeek: bookingData.day,
          timeSlot: formData1.batchTime,
        };

        await fetch(`https://api.kunalsdrumclass.com/api/InsertSwapStudentResponse`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        await Swal.fire({
          title: "Success!",
          text: "details submitted successfully!",
          icon: "success",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-primary",
          },
        });
      } else if (formData1.bookingType === "Comp") {
        const bookingData = getBookingClassDate(formData1.batchTime);
        const payload = {
          studentId: formData1.studentId,
          compId: 0,
          classDate: bookingData.classDate,
          day: bookingData.day,
          time: formData1.batchTime,
        };

        const res = await fetch(`https://api.kunalsdrumclass.com/api/InsertStudentBooking`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (data.recordsets?.[0][0].Status === 2) {
          await Swal.fire({
            title: "Slot Full",
            text: "slot is full for this time. please select another time.",
            icon: "warning",
            customClass: {
              popup: "swal2-material-ui",
              confirmButton: "btn btn-sm rounded-8 btn-warning",
            },
          });
        } else {
          await Swal.fire({
            title: "Success!",
            text: "details submitted successfully!",
            icon: "success",
            customClass: {
              popup: "swal2-material-ui",
              confirmButton: "btn btn-sm rounded-8 btn-primary",
            },
          });
        }
      }
      setLoading(false);
      closeAddModal();
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEmergencyStudent = async () => {
    try {
      setLoading(true);
      const payload = {
        studentId: formData2.studentId,
        classDate: moment().format("YYYY-MM-DD"),
        day: moment().format("dddd"),
        time: formData2.batchTime,
        bookingType: formData2.bookingType,
      };

      await fetch(`https://api.kunalsdrumclass.com/api/InsertEmergencyStudentBooking`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      await Swal.fire({
        title: "Success!",
        text: "details submitted successfully!",
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      setLoading(false);
      closeEmergencyModal();
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  const showAddButton = useMemo(() => {
    const now = moment();
    const currentDay = now.day();
    const currentHourMinute = now.hours() * 60 + now.minutes();

    if (currentDay === 6 || currentDay === 0) {
      if (currentDay === 6) {
        const friday3PM = 15 * 60;
        const saturday10PM = 22 * 60;

        return currentHourMinute >= friday3PM ||
          currentHourMinute <= saturday10PM;
      }

      if (currentDay === 0) {
        return currentHourMinute <= 22 * 60;
      }
    }

    const startTime = 19 * 60;
    const endTime = 22 * 60;

    if (currentDay === 1) {
      return currentHourMinute <= endTime;
    }

    return currentHourMinute >= startTime ||
      currentHourMinute <= endTime;

  }, []);

  const showEmergencyButton = useMemo(() => {
    const now = moment();
    const currentDay = now.day();
    const currentMinutes = now.hours() * 60 + now.minutes();

    if (currentDay >= 1 && currentDay <= 5) {
      const startTime = (15 * 60) + 30;
      const endTime = (23 * 60) + 59;

      return currentMinutes >= startTime &&
        currentMinutes <= endTime;
    }

    const weekendStart = (9 * 60) + 30;
    const weekendEnd = (23 * 60) + 59;

    return currentMinutes >= weekendStart &&
      currentMinutes <= weekendEnd;

  }, []);

  const isTempResponseTime = useMemo(() => {
    const now = moment();
    const tomorrow = moment().add(1, "day");
    const day = tomorrow.day();
    const currentMinutes = now.hours() * 60 + now.minutes();

    if (day >= 1 && day <= 5) {
      const startTime = 19 * 60;
      const endTime = 20 * 60;

      return currentMinutes >= startTime && currentMinutes <= endTime;
    }

    const startTime = 15 * 60;
    const endTime = 16 * 60;

    return currentMinutes >= startTime && currentMinutes <= endTime;
  }, []);

  const handleResponse = useCallback(async (rowData, status) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to mark this student response as ${status.toLowerCase()} ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      await fetch(
        "https://api.kunalsdrumclass.com/api/InsertUpdateStudentResponse",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studentId: rowData.Student_Id,
            studentClassId: rowData.Student_Class_Id,
            classDate: rowData.Class_Date,
            dayOfWeek: rowData.Day_Of_Week,
            timeSlot: rowData.Time_Slot,
            willAttend: status === "Yes" ? true : false,
            reason: status === "No" ? "Response submitted by admin." : "",
          }),
        }
      );

      await Swal.fire({
        title: "Success!",
        text: `Student response submitted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      setTimeSlot(rowData.Time_Slot);
      await fetchData();
    } catch {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  }, [token, fetchData]);

  const columns = useMemo(() => {
    if (selectedDayTab === "temp-list") {
      return [
        {
          header: "Name",
          accessorKey: "Student_Name",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Contact No",
          accessorKey: "Contact_Number",
          enableSorting: true,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;
            const mobile = rowData.Contact_Number || "";
            const cleanNumber = mobile.replace(/\D/g, "");
            const email = rowData.Email_Address || "";

            return (
              <div className="d-flex flex-column gap-1">
                <span>{mobile}</span>

                <div className="d-flex gap-2 align-items-center">
                  <a
                    href={`tel:+${cleanNumber}`}
                    title="Call"
                  >
                    <i className="mdi mdi-phone-outline fs-3"></i>
                  </a>

                  <a
                    href={`https://wa.me/${cleanNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    title="WhatsApp"
                  >
                    <i className="mdi mdi-whatsapp fs-3"></i>
                  </a>

                  <a
                    href={
                      /Mobi|Android/i.test(navigator.userAgent)
                        ? `mailto:${email}`
                        : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Send Email"
                  >
                    <i className="mdi mdi-email-send-outline fs-3"></i>
                  </a>
                </div>
              </div>
            );
          },
        },
        {
          header: "Date",
          accessorKey: "Class_Date",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Day",
          accessorKey: "Day_Of_Week",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Time",
          accessorKey: "Time_Slot",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Class Type",
          accessorKey: "Booking_Type",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Response",
          accessorKey: "Will_Attend",
          enableSorting: true,
          enableColumnFilter: false,
          cell: ({ row }) => {
            const rowData = row.original;
            const value = row.original.Will_Attend;
            const values = row.original.Booking_Type;

            if (value === 1 || value === "1" || value === "Yes") {
              return (
                <button className="btn btn-sm btn-success" disabled={!isTempResponseTime}>
                  Yes
                </button>
              );
            }

            if (value === 0 || value === "0" || value === "No") {
              return (
                <button className="btn btn-sm btn-danger" disabled={!isTempResponseTime}>
                  No
                </button>
              );
            }

            if (values === "Comp") {
              return (
                <button className="btn btn-sm btn-success" disabled={!isTempResponseTime}>
                  Yes
                </button>
              );
            }

            return (
              <>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-success" onClick={() => handleResponse(rowData, "Yes")} disabled={!isTempResponseTime}>
                    Mark Yes
                  </button>

                  <button className="btn btn-sm btn-danger" onClick={() => handleResponse(rowData, "No")} disabled={!isTempResponseTime}>
                    Mark No
                  </button>
                </div>
              </>
            );
          },
        },
      ];
    }

    return [
      {
        header: "Name",
        meta: {
          sticky: true,
        },
        accessorKey: "Student_Name",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const value = row.original.Student_Id;
          const currentName = row.original.Student_Name;

          const mergedOptions = useMemo(() => {
            const exists = compStudents.some(s => s.Id == value);

            if (!value || exists) return compStudents;

            return [
              {
                Id: value,
                Student_Name: currentName || `Student ${value}`,
              },
              ...compStudents,
            ];
          }, [compStudents, value, currentName]);

          return (
            <select
              className="form-select"
              value={value || ""}
              style={{ minWidth: "150px" }}
              disabled={!isEditableTime || !canEdit}
              onChange={(e) =>
                handleStudentChange(row.index, e.target.value)
              }
            >
              <option value="">Select Student</option>

              {mergedOptions.map((student) => (
                <option key={student.Id} value={student.Id}>
                  {student.Student_Name}
                </option>
              ))}
            </select>
          );
        }
      },
      {
        header: "Contact No",
        accessorKey: "Contact_Number",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const mobile = rowData.Contact_Number;
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email_Address;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{rowData.Contact_Number}</span>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3"></i>
                </a>
                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3"></i>
                </a>
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3"></i>
                </a>

              </div>
            </div>
          );
        },
      },
      {
        header: "Date",
        accessorKey: "Class_Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Day",
        accessorKey: "Day_Of_Week",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Time",
        accessorKey: "Time_Slot",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Class Type",
        accessorKey: "Booking_Type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "Status",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {

          const classDateTime = moment(
            `${row.original.Class_Date} ${row.original.Time_Slot.split("-")[0].trim()}`,
            ["YYYY-MM-DD h A", "YYYY-MM-DD h:mm A", "YYYY-MM-DD HH:mm"]
          );

          const disableTime = classDateTime.clone().subtract(30, "minutes");
          const isDisabled = moment().isAfter(disableTime);

          return (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={isDisabled}
              onClick={() => handleRemove(row.original)}
            >
              Remove
            </button>
          );
        },
      }
    ];
  }, [selectedDayTab, compStudents, isEditableTime, canEdit, handleResponse]);

  const handleRemove = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: "Are you sure you want to remove this student?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    try {

      const payload1 = {
        oldStudentId: rowData.Student_Id,
        classDate: rowData.Class_Date,
        dayOfWeek: rowData.Day_Of_Week,
        timeSlot: rowData.Time_Slot,
        bookingType: rowData.Booking_Type,
        status: rowData.Status,
      };

      await fetch("https://api.kunalsdrumclass.com/api/RemoveStudentFromFinalList", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload1),
      });

      await Swal.fire({
        title: "Success!",
        text: "Student removed successfully!",
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchData();

    } catch (error) {
      console.error(error);
    }
  };

  //meta title
  document.title = "Students Final List | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Students Final List"
          breadcrumbItems={[
            { title: "Students Final List" }
          ]}
        />

        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Nav tabs>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedDayTab === "final-list",
                  })}
                  onClick={() => handleDayTabChange("final-list")}
                >
                  Final List
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedDayTab === "temp-list",
                  })}
                  onClick={() => handleDayTabChange("temp-list")}
                >
                  Temp List
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          {selectedDayTab === "final-list" && (
            <div className="d-flex justify-content-end mb-3">
              <button type="button" className="btn btn-primary me-2" onClick={openCloseModal}>
                Close Class
              </button>

              {showAddButton && (
                <button type="button" className="btn btn-primary me-2" onClick={openAddModal}>
                  + Swap/Add
                </button>
              )}

              {showEmergencyButton && (
                <button type="button" className="btn btn-primary" onClick={openEmergencyModal}>
                  + Emergency
                </button>
              )}
            </div>
          )}
        </div>

        {isClassHoliday && (
          <div>
            <p className="fw-semibold text-danger mt-3">
              Note: {holidayMessage}
            </p>
          </div>
        )}

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          classDate={classDate}
          timeSlot={timeSlot}
          timeSlots={timeSlots}
          totalRecords={totalRecords}
          sortingState={sortingState}
          isClassCancelled={isClassCancelled}
          canShowAttendance={true}
          onPageChange={handlePageChange}
          onTimeSlotChange={handleTimeSlotChange}
          onSortChange={handleSortChange}
          isLoading={loading}
          isPagination
          isCustomPageSize
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <CloseClassModal
          show={showCloseModal}
          loading={loading}
          formData3={formData3}
          setFormData3={setFormData3}
          timeSlots={timeSlots}
          attendanceTakenSlots={attendanceTakenSlots}
          onSubmit={handleCloseClass}
          onClose={closeCloseModal}
        />

        <AddStudentModel
          show={showModal}
          loading={loading}
          formData1={formData1}
          setFormData1={setFormData1}
          allStudents={allStudents}
          compStudents={compStudents}
          timeSlots={modalTimeSlots}
          batchStudentCount={modalBatchStudentCount}
          onSubmit={handleAddStudent}
          onClose={closeAddModal}
        />

        <EmergencyStudentModel
          show={showEmergencyModal}
          loading={loading}
          formData2={formData2}
          setFormData2={setFormData2}
          allStudents={allStudents}
          compStudents={compStudents}
          timeSlots={timeSlots}
          onSubmit={handleEmergencyStudent}
          onClose={closeEmergencyModal}
        />
      </div>
    </div>
  );
};

export default DatatableTables;
